<template>
	<div class="image-uploader">
		<div class="card">
			<div class="card-body">
				<div
					class="drop-zone"
					:class="{ 'drag-over': isDragging }"
					@dragover.prevent="onDragOver"
					@dragleave.prevent="onDragLeave"
					@drop.prevent="onDrop"
					@click="triggerFileInput"
				>
					<div v-if="!isUploading && !files.length" class="text-center py-5">
						<i class="bi bi-cloud-arrow-up display-4"></i>
						<h5 class="mt-3">Drag and drop images here or click to select</h5>
						<p class="text-muted small">You can upload up to 20 images at once</p>
					</div>

					<div v-else-if="isUploading" class="upload-status p-3">
						<h5>Uploading {{ uploadProgress.completed }} of {{ uploadProgress.total }} files</h5>
						<div class="mb-3">
							<div class="d-flex justify-content-between mb-1">
								<span>Overall Progress</span>
								<span>{{ uploadProgress.percentage.toFixed(0) }}%</span>
							</div>
							<div class="progress" style="height: 20px;">
								<div
									class="progress-bar progress-bar-striped progress-bar-animated"
									role="progressbar"
									:style="{ width: `${uploadProgress.percentage}%` }"
									:aria-valuenow="uploadProgress.percentage"
									aria-valuemin="0"
									aria-valuemax="100">
								</div>
							</div>
						</div>

						<!-- Individual file progress cards -->
						<div class="row g-3">
							<div v-for="(file, index) in uploadingFiles" :key="index" class="col-6 col-md-4 col-lg-3">
								<div class="card h-100 file-progress-card">
									<div class="card-body p-2">
										<div class="image-preview mb-2">
											<img :src="prepareImageSource(file)" alt="Preview" class="img-fluid rounded" />
											<div class="progress-overlay d-flex align-items-center justify-content-center">
												<div class="progress-percentage">{{ file.progress.toFixed(0) }}%</div>
											</div>
										</div>
										<div class="text-truncate small mb-1">{{ file.name }}</div>
										<div class="progress" style="height: 8px;">
											<div
												class="progress-bar"
												role="progressbar"
												:class="{'bg-success': file.progress >= 100}"
												:style="{ width: `${file.progress}%` }"
												:aria-valuenow="file.progress"
												aria-valuemin="0"
												aria-valuemax="100">
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div v-else class="p-3">
						<div class="row g-3 preview-container">
							<div v-for="(file, index) in files" :key="index" class="col-6 col-sm-4 col-md-3 col-lg-2">
								<div class="preview-item card h-100 mb-0">
									<div class="image-wrapper">
										<img :src="prepareImageSource(file)" alt="Preview" class="card-img-top preview-image" />
										<button
											@click.stop="removeFile(index)"
											class="btn btn-danger btn-sm position-absolute rounded-circle end-0 top-0 m-1"
											title="Remove image"
										>
											<i class="bi bi-x"></i>
										</button>
									</div>
									<div class="card-footer p-1 text-center">
										<small class="text-truncate d-block">{{ file.name }}</small>
										<!-- <small class="text-muted">{{ formatFileSize(file.size) }}</small> -->

										<!-- File type indicator -->
										<!-- <div class="mt-1">
											<span v-if="file.type.includes('image')" class="badge bg-success me-1">
												<i class="bi bi-image me-1"></i>Image
											</span>
											<span v-else-if="file.type.includes('pdf')" class="badge bg-danger me-1">
												<i class="bi bi-file-pdf me-1"></i>PDF
											</span>
											<span v-else-if="file.type.includes('msword') || file.type.includes('document')" class="badge bg-primary me-1">
												<i class="bi bi-file-word me-1"></i>DOC
											</span>
											<span v-else class="badge bg-secondary me-1">
												<i class="bi bi-file me-1"></i>File
											</span>
										</div> -->

										<!-- Compression status (for all files) -->
										<!-- <div v-if="file.compressed" class="mt-1">
											<small class="text-success">
												<i class="bi bi-check-circle me-1"></i>
												Compressed: {{ formatFileSize(file.originalSize) }} → {{ formatFileSize(file.size) }}
											</small>
											<br>
											<small class="text-muted">
												Saved {{ Math.round(((file.originalSize - file.size) / file.originalSize) * 100) }}%
											</small>
										</div> -->
									</div>
								</div>
							</div>
						</div>

						<div class="col-6 col-sm-4 col-md-3 col-lg-2">
							<div
								class="add-more card h-100 d-flex justify-content-center align-items-center"
								@click.stop="triggerFileInput"
							>
								<i class="bi bi-plus-lg display-6"></i>
							</div>
						</div>
					</div>
				</div>

				<input
					type="file"
					ref="fileInput"
					multiple
					@change="onFileSelected"
					class="file-input d-none"
				/>
				<div v-if="isVideoCompressing" class="alert alert-info text-center my-3">
                    <Loader text="Optimizing video, please wait..." />
                </div>
			</div>

			<div v-if="files.length && !isUploading" class="card-footer d-flex gap-2 justify-content-end">
				<button @click="clearFiles" class="btn btn-secondary">
					<i class="bi bi-trash me-1"></i> Clear
				</button>
				<button @click="uploadFiles" class="btn btn-primary">
					<i class="bi bi-upload me-1"></i> Upload {{ files.length }} Images
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import Loader from './Loader.vue';

export default {
	name: 'ImageUploader',
    components: { Loader },
    props: {
        orderId: {
            type: Number,
            required: false,
            default: null
        },
        projectId: {
            type: Number,
            required: false,
            default: null
        }
    },
	data() {
        return {
            isDragging: false,
		isVideoCompressing: false, // Track video compression state
            files: [],
            uploadedFiles: [],
            uploadingFiles: [], // Track files currently uploading with their progress
            isUploading: false,
            uploadProgress: {
                total: 0,
                completed: 0,
                percentage: 0,
                fileProgress: {}
            },
            chunkSize: 2 * 1024 * 1024, // 2MB chunks (will be adjusted based on network)
            concurrentUploads: 3, // Will be adjusted based on device type
            maxFiles: 20,
            maxFileSize: 512 * 1024 * 1024, // 512MB max file size
            urlCopied: false,
            copiedFileId: null,
            toastVisible: false,
            toastMessage: '',
            batchId: Date.now(),
            
            // ===== NEW: NETWORK & DEVICE DETECTION =====
            isMobile: /Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
            isOnline: navigator.onLine,
            networkType: 'unknown',
            effectiveConnectionType: '4g',
            
            // ===== NEW: RETRY CONFIGURATION =====
            retryConfig: {
                maxRetries: 3,           // Maximum retry attempts per chunk
                baseDelay: 1000,         // Base delay for exponential backoff (1 second)
                maxDelay: 10000,         // Maximum delay between retries (10 seconds)
                timeoutPerChunk: 30000   // 30 second timeout per chunk upload
            },
            
            // ===== NEW: UPLOAD STATE TRACKING =====
            uploadsPaused: false,
            pausedUploads: new Set(),
            failedChunks: new Map(), // Track failed chunks for retry
            
			// Auto compression settings (no UI)
			autoCompress: true,
			compressionSettings: {
				imageQuality: 0.9, // 90% quality as requested
				imageMaxWidth: 1920, // Keep 1920x1080 resolution
				imageMaxHeight: 1080,
				imageFormat: 'image/webp', // WebP for better compression
				targetFileSize: 100 * 1024, // 100KB target
				enableMultiPass: true,
				enableSmartResize: true,
				maxIterations: 10,
				minQuality: 0.3,
				aggressiveMode: true,

				// UPDATED VIDEO COMPRESSION SETTINGS FOR BETTER PERFORMANCE
                videoQuality: 0.8, // 80% quality for videos
                videoMaxWidth: 1920,
                videoMaxHeight: 1080,
                videoFormat: 'video/webm', // WebM for better compression
                videoTargetFileSize: 5 * 1024 * 1024, // 5MB target for videos
                videoBitrate: 2000000, // INCREASED from 1000000 to 2000000 (2Mbps) for better quality/speed balance
                videoFrameRate: 30, // 30fps
                enableVideoMultiPass: false, // CHANGED from true to false for faster processing
                videoMaxIterations: 3, // REDUCED from 5 to 3 iterations
                videoMinQuality: 0.5, // INCREASED from 0.4 to 0.5 (higher minimum quality)
                
                // NEW PERFORMANCE SETTINGS (ADD THESE)
                useHardwareAcceleration: true,
                preferGPUProcessing: true,
                optimizeForSpeed: true,
                maxProcessingTime: 60000, // 1 minute timeout
                enableWebCodecs: true, // Enable WebCodecs API if available
                preferredCodec: 'vp9', // Prefer VP9 codec for better compression
                chunkProcessing: true, // Enable chunk-based processing
                backgroundProcessing: true // Allow background processing
			}
        };
	},
    computed: {
        uploadType() {
            return this.projectId ? 'project' : 'order';
        },
        finalizeEndpoint() {
            return this.uploadType === 'project' 
                ? '/api/handyman/projects/uploads/finalize' 
                : '/api/handyman/uploads/finalize';
        },
        historyEndpoint() {
            return this.uploadType === 'project' 
                ? '/api/handyman/projects/uploads/generate-history' 
                : '/api/handyman/uploads/generate-history';
        }
    },
    mounted() {
        // Initialize network and device detection
        this.detectNetworkSpeed();
        this.adjustUploadSettings();
        
        // Listen for online/offline events
        window.addEventListener('online', this.handleOnline);
        window.addEventListener('offline', this.handleOffline);
        
        // Listen for connection changes (if supported)
        if ('connection' in navigator) {
            const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            if (connection) {
                connection.addEventListener('change', this.detectNetworkSpeed);
            }
        }
        
        console.log('[ImageUploader] Initialized', {
            isMobile: this.isMobile,
            networkType: this.effectiveConnectionType,
            concurrentUploads: this.concurrentUploads,
            chunkSize: this.formatFileSize(this.chunkSize)
        });
    },
    beforeUnmount() {
        // Cleanup event listeners
        window.removeEventListener('online', this.handleOnline);
        window.removeEventListener('offline', this.handleOffline);
        
        if ('connection' in navigator) {
            const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            if (connection) {
                connection.removeEventListener('change', this.detectNetworkSpeed);
            }
        }
    },
	methods: {
        triggerFileInput() {
            this.$refs.fileInput.click();
            this.batchId = Date.now();
        },

        // ===== NEW: NETWORK DETECTION =====
        detectNetworkSpeed() {
            const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            
            if (connection && connection.effectiveType) {
                this.effectiveConnectionType = connection.effectiveType;
                this.networkType = connection.type || 'unknown';
                
                console.log('[ImageUploader] Network detected:', {
                    type: this.networkType,
                    effectiveType: this.effectiveConnectionType,
                    downlink: connection.downlink,
                    rtt: connection.rtt
                });
                
                // Adjust settings when network changes
                this.adjustUploadSettings();
            } else {
                // Fallback: assume 4g
                this.effectiveConnectionType = '4g';
            }
        },

        // ===== NEW: ADAPTIVE UPLOAD SETTINGS =====
        adjustUploadSettings() {
            const ect = this.effectiveConnectionType;
            
            // Adjust chunk size based on network speed
            if (ect === 'slow-2g' || ect === '2g') {
                this.chunkSize = 512 * 1024; // 512KB for slow networks
                this.retryConfig.timeoutPerChunk = 60000; // 60s timeout
                this.concurrentUploads = 1; // One at a time
            } else if (ect === '3g') {
                this.chunkSize = 1 * 1024 * 1024; // 1MB for 3G
                this.retryConfig.timeoutPerChunk = 45000; // 45s timeout
                this.concurrentUploads = this.isMobile ? 1 : 2;
            } else if (ect === '4g') {
                this.chunkSize = 2 * 1024 * 1024; // 2MB for 4G
                this.retryConfig.timeoutPerChunk = 30000; // 30s timeout
                this.concurrentUploads = this.isMobile ? 1 : 2;
            } else {
                // Assume fast connection (5G, wifi)
                this.chunkSize = 5 * 1024 * 1024; // 5MB for fast connections
                this.retryConfig.timeoutPerChunk = 30000; // 30s timeout
                this.concurrentUploads = this.isMobile ? 2 : 3;
            }
            
            // Force lower concurrency on mobile regardless of network
            if (this.isMobile) {
                this.concurrentUploads = Math.min(this.concurrentUploads, 1);
            }
            
            console.log('[ImageUploader] Settings adjusted:', {
                chunkSize: this.formatFileSize(this.chunkSize),
                concurrentUploads: this.concurrentUploads,
                timeout: this.retryConfig.timeoutPerChunk + 'ms'
            });
        },

        // ===== NEW: OFFLINE/ONLINE HANDLERS =====
        handleOffline() {
            console.log('[ImageUploader] Connection lost - pausing uploads');
            this.isOnline = false;
            this.uploadsPaused = true;
            
            this.$notification('warning', 'You are offline. Uploads will resume when connection is restored.');
        },

        handleOnline() {
            console.log('[ImageUploader] Connection restored - resuming uploads');
            this.isOnline = true;
            this.uploadsPaused = false;
            
            this.$notification('success', 'Back online! Resuming uploads...');
            
            // Re-detect network speed
            this.detectNetworkSpeed();
        },

        // ===== NEW: RETRY MECHANISM WITH EXPONENTIAL BACKOFF =====
        async uploadChunkWithRetry(formData, attempt = 0) {
            try {
                // Wait if uploads are paused (offline)
                while (this.uploadsPaused) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
                
                // Create abort controller for timeout
                const controller = new AbortController();
                const timeoutId = setTimeout(
                    () => controller.abort(),
                    this.retryConfig.timeoutPerChunk
                );
                
                const response = await axios.post('/api/handyman/uploads/chunk', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    signal: controller.signal,
                    timeout: this.retryConfig.timeoutPerChunk,
                    onUploadProgress: formData.progressCallback || null
                });
                
                clearTimeout(timeoutId);
                return response;
                
            } catch (error) {
                const isTimeout = error.code === 'ECONNABORTED' || error.name === 'AbortError';
                const isNetworkError = error.message.includes('Network Error') || !error.response;
                const is5xxError = error.response?.status >= 500;
                
                // Retry on timeout, network error, or server error
                const shouldRetry = (isTimeout || isNetworkError || is5xxError) && attempt < this.retryConfig.maxRetries;
                
                if (shouldRetry) {
                    // Calculate delay with exponential backoff
                    const delay = Math.min(
                        this.retryConfig.baseDelay * Math.pow(2, attempt),
                        this.retryConfig.maxDelay
                    );
                    
                    console.log(`[ImageUploader] Chunk upload failed (attempt ${attempt + 1}/${this.retryConfig.maxRetries}), retrying in ${delay}ms...`, {
                        error: error.message,
                        isTimeout,
                        isNetworkError
                    });
                    
                    // Wait before retrying
                    await new Promise(resolve => setTimeout(resolve, delay));
                    
                    // Retry
                    return this.uploadChunkWithRetry(formData, attempt + 1);
                } else {
                    // Max retries exceeded or non-retryable error
                    console.error('[ImageUploader] Chunk upload failed permanently:', error);
                    throw error;
                }
            }
        },

        onDragOver(e) {
            this.isDragging = true;
        },

        onDragLeave(e) {
            this.isDragging = false;
        },

        onDrop(e) {
            this.isDragging = false;
            //this.handleFiles(e.dataTransfer.files);

            // First check for regular files
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                this.handleFiles(e.dataTransfer.files);
                return;
            }

            // Check for URL-based transfers (like from WhatsApp Web)
            const html = e.dataTransfer.getData('text/html');
            const text = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
            
            if (html || text) {
                // Extract image URL from HTML or plain text
                let imageUrl = null;
                
                if (html) {
                    // Look for img tags in HTML
                    const imgMatch = html.match(/<img[^>]+src="([^"]+)"/);
                    if (imgMatch) {
                        imageUrl = imgMatch[1];
                    }
                }
                
                if (!imageUrl && text) {
                    // Check if text is a direct image URL
                    imageUrl = text.trim();
                }
                
                if (imageUrl) {
                    this.fetchImageFromUrl(imageUrl);
                }
            }
        },
        async fetchImageFromUrl(url) {
            try {
                // Fetch the image from URL
                const response = await fetch(url);
                const blob = await response.blob();
                
                // Convert blob to File object
                const fileName = url.split('/').pop() || 'whatsapp-image.jpg';
                const file = new File([blob], fileName, { type: blob.type });
                
                // Handle the file
                this.handleFiles([file]);
            } catch (error) {
                console.error('Error fetching image from URL:', error);
                this.$notification('error', 'Unable to fetch image from WhatsApp Web. Please download the image first and then upload it.');
            }
        },

        onFileSelected(e) {
            this.handleFiles(e.target.files);
            // Reset the input so the same file can be selected again
            this.$refs.fileInput.value = '';
        },

        async handleFiles(fileList) {
            this.batchId = Date.now();

            // Check if adding these files would exceed max files
            if (this.files.length + fileList.length > this.maxFiles) {
                alert(`You can only upload up to ${this.maxFiles} images at once.`);
                return;
            }

			for (const file of Array.from(fileList)) {
                // Check supported file types
                const supportedTypes = [
                    'image/', 'application/pdf', 'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'text/', 'application/zip', 'video/'
                ];

                const isSupported = supportedTypes.some(type => file.type.startsWith(type));
                if (!isSupported) {
                    alert(`File "${file.name}" is not a supported file type.`);
                    continue;
                }

                // Check file size
                if (file.size > this.maxFileSize) {
                    alert(`File "${file.name}" exceeds the maximum size of ${this.formatFileSize(this.maxFileSize)}`);
                    continue;
                }

				// Auto-compress all files based on type
                let processedFile = file;
                let isCompressed = false;

                try {
                    if (file.type.startsWith('image/')) {
                        // Compress images using Canvas API
                        processedFile = await this.compressImage(file);
                        isCompressed = true;
                    } else if (file.type.startsWith('video/')) {
                        // Compress video files
                        processedFile = file; //await this.compressVideo(file);
                        isCompressed = true;
                    } else {
                        // For non-image, non-video files, apply generic compression
                        // processedFile = await this.compressGenericFile(file);
                        // isCompressed = processedFile.size < file.size;
						processedFile = file;
                        isCompressed = false;
                    }

                    if (isCompressed) {
                        console.log(`Compressed ${file.name}: ${this.formatFileSize(file.size)} → ${this.formatFileSize(processedFile.size)}`);
                    }
                } catch (error) {
                    console.error('Compression failed, using original file:', error);
                    processedFile = file;
                    isCompressed = false;
                }

                // Create preview - await to ensure sequential processing
                // Store file references in separate constants to avoid closure issues
                await new Promise((resolve) => {
                    const reader = new FileReader();
                    // Create immutable references to avoid closure problems
                    const fileForUpload = processedFile;
                    const originalFileName = file.name;
                    const originalFileSize = file.size;
                    const originalFileType = file.type;
                    const processedFileSize = processedFile.size;
                    const wasCompressed = isCompressed;
                    
                    reader.onload = (e) => {
                        this.files.push({
                            file: fileForUpload,
                            preview: e.target.result,
                            name: originalFileName,
                            size: processedFileSize,
                            type: originalFileType,
                            progress: 0,
                            originalSize: originalFileSize,
                            compressed: wasCompressed,
                            isImage: originalFileType.startsWith('image/'),
                            isPdf: originalFileType === 'application/pdf',
                            isDoc: originalFileType.includes('word') || originalFileType.includes('document')
                        });
                        resolve();
                    };
                    reader.onerror = () => resolve();
                    // Read from the file for preview
                    reader.readAsDataURL(file);
                });
            }
        },

        removeFile(index) {
            this.files.splice(index, 1);
        },

        clearFiles() {
            this.files = [];
        },

        clearUploadedFiles() {
            if (confirm('Are you sure you want to clear all uploaded files?')) {
            this.uploadedFiles = [];
            }
        },

        removeUploadedFile(index) {
            if (confirm('Are you sure you want to remove this file?')) {
            // Here you would typically make an API call to delete the file on the server
            // For now, we'll just remove it from the front-end
            this.uploadedFiles.splice(index, 1);
            }
        },

        openImageInNewTab(url) {
            window.open(url, '_blank');
        },

        copyImageUrl(url) {
            navigator.clipboard.writeText(url).then(() => {
            this.showToast('URL copied to clipboard!');
            });
        },

        showToast(message) {
            this.toastMessage = message;
            this.toastVisible = true;
            setTimeout(() => {
            this.toastVisible = false;
            }, 2000);
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';

            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));

            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        async uploadFiles() {
            if (!this.files.length) return;

            this.isUploading = true;
            this.uploadingFiles = [...this.files];
            this.uploadProgress = {
                total: this.files.length,
                completed: 0,
                percentage: 0,
                fileProgress: {}
            };

            // Create a queue of files to upload
            const queue = [...this.files];
            const activeUploads = new Set();

            // Process uploads
            const processQueue = async () => {
                if (queue.length === 0 && activeUploads.size === 0) {
                    // All uploads finished
                    this.isUploading = false;
                    this.files = [];
                    this.uploadingFiles = [];

                    // Display notification
                    this.$notification('success', 'All files uploaded successfully');

                    // Track image upload history
                    const historyPayload = this.uploadType === 'project'
                        ? { project_id: this.projectId, images: this.uploadedFiles }
                        : { order_id: this.orderId, batch_id: this.batchId, action: 'generate_history' };

                    axios.post(this.historyEndpoint, historyPayload)
                    .then(response => {
                        // Emit event to notify parent that all uploads are complete
                        this.$emit('uploads-completed', this.uploadedFiles);
                    })
                    .catch(error => {
                        console.log(error);
                    });

                    // Reset batch ID
                    this.batchId = Date.now();

                    return;
                }

                // Start new uploads if there's capacity and files in the queue
                while (queue.length > 0 && activeUploads.size < this.concurrentUploads) {
                    const fileData = queue.shift();
                    activeUploads.add(fileData);

                    this.uploadFile(fileData)
                        .then(response => {
                            // Add to uploaded files
                            this.uploadedFiles.push({
                                name: fileData.name,
                                url: response.url,
                                id: response.id,
                                stored_name: response.stored_name,
                                file_type: response.file_type,
                                file_size: response.file_size
                            });

                            this.uploadProgress.completed++;
                            this.uploadProgress.percentage = (this.uploadProgress.completed / this.uploadProgress.total) * 100;

                            // Remove from active uploads
                            activeUploads.delete(fileData);

                            // Continue processing queue
                            processQueue();
                        })
                        .catch(error => {
                            console.error(`Error uploading ${fileData.name}:`, error);
                            // Show more detailed error message
                            alert(`Failed to upload ${fileData.name}: ${error.message || 'Unknown error'}`);

                            this.uploadProgress.completed++;
                            this.uploadProgress.percentage = (this.uploadProgress.completed / this.uploadProgress.total) * 100;

                            // Remove from active uploads
                            activeUploads.delete(fileData);

                            // Continue processing queue
                            processQueue();
                        });
                }
            };

            // Start processing
            processQueue();
        },

        async uploadFile(fileData) {
            const file = fileData.file;
            
            // ===== ADAPTIVE CHUNK SIZE =====
            // Use adaptive chunk size based on network and file type
            let chunkSize = this.chunkSize; // Already adjusted based on network
            
            // For videos, use larger chunks if network is good
            if (file.type.startsWith('video/')) {
                if (this.effectiveConnectionType === '4g' || this.effectiveConnectionType === '5g') {
                    chunkSize = 10 * 1024 * 1024; // 10MB for videos on good network
                } else {
                    chunkSize = 2 * 1024 * 1024; // 2MB for videos on slower networks
                }
            }
            
            const totalChunks = Math.ceil(file.size / chunkSize);
            
            console.log(`[ImageUploader] Uploading file: ${file.name}`, {
                size: this.formatFileSize(file.size),
                chunks: totalChunks,
                chunkSize: this.formatFileSize(chunkSize),
                network: this.effectiveConnectionType
            });

            try {
                // Initialize file upload on the server
                const initResponse = await axios.post('/api/handyman/uploads/init', {
                    filename: file.name,
                    filesize: file.size,
                    type: file.type,
                    chunks: totalChunks
                }, {
                    timeout: 10000 // 10s timeout for init
                });

                const fileId = initResponse.data.file_id;

                // Find the index of this file in the uploadingFiles array
                const fileIndex = this.uploadingFiles.findIndex(f =>
                    f.name === fileData.name && f.size === fileData.size
                );

                // Set initial progress
                if (fileIndex !== -1) {
                    this.uploadingFiles[fileIndex].progress = 0;
                }

                // ===== UPLOAD EACH CHUNK WITH RETRY =====
                for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                    const start = chunkIndex * chunkSize;
                    const end = Math.min(start + chunkSize, file.size);
                    const chunk = file.slice(start, end);

                    const formData = new FormData();
                    formData.append('file', chunk);
                    formData.append('file_id', fileId);
                    formData.append('chunk_index', chunkIndex);
                    formData.append('total_chunks', totalChunks);

                    // Store reference to component instance
                    const self = this;
                    const currentFileIndex = fileIndex;
                    
                    // Add progress callback to formData (custom property)
                    formData.progressCallback = function(progressEvent) {
                        // Calculate the chunk progress
                        const chunkProgress = (progressEvent.loaded / progressEvent.total) * 100;

                        // Calculate overall file progress based on chunks
                        const fileProgress = ((chunkIndex + (chunkProgress / 100)) / totalChunks) * 100;

                        // Update progress in the uploadingFiles array
                        if (currentFileIndex !== -1 && self.uploadingFiles[currentFileIndex]) {
                            self.uploadingFiles[currentFileIndex].progress = fileProgress;
                        }
                    };

                    try {
                        // ===== USE NEW RETRY MECHANISM =====
                        await this.uploadChunkWithRetry(formData);

                        // Update progress after each chunk is complete
                        if (fileIndex !== -1 && this.uploadingFiles[fileIndex]) {
                            const completedChunkProgress = ((chunkIndex + 1) / totalChunks) * 100;
                            this.uploadingFiles[fileIndex].progress = completedChunkProgress;
                        }
                        
                        console.log(`[ImageUploader] Chunk ${chunkIndex + 1}/${totalChunks} uploaded successfully for ${file.name}`);
                        
                    } catch (chunkError) {
                        console.error(`[ImageUploader] Chunk ${chunkIndex} failed permanently for ${file.name}:`, chunkError);
                        throw new Error(`Failed to upload chunk ${chunkIndex + 1}/${totalChunks}: ${chunkError.response?.data?.message || chunkError.message}`);
                    }
                }

                // Complete the file upload
                try {
                    const finalizePayload = this.uploadType === 'project'
                        ? { file_id: fileId, project_id: this.projectId, batch_id: this.batchId }
                        : { file_id: fileId, order_id: this.orderId, batch_id: this.batchId };

                    const finalizeResponse = await axios.post(this.finalizeEndpoint, finalizePayload);

                    // Set progress to 100% when complete
                    if (fileIndex !== -1 && this.uploadingFiles[fileIndex]) {
                        this.uploadingFiles[fileIndex].progress = 100;
                    }

                    return {
                        url: finalizeResponse.data.url,
                        id: finalizeResponse.data.id,
                        stored_name: finalizeResponse.data.stored_name,
                        file_type: finalizeResponse.data.file_type,
                        file_size: finalizeResponse.data.file_size
                    };
                } catch (finalizeError) {
                    console.error(`Error finalizing upload for ${file.name}:`, finalizeError);
                    throw new Error(`Failed to finalize upload: ${finalizeError.response?.data?.message || finalizeError.message}`);
                }
            } catch (error) {
                console.error(`Complete error details for ${file.name}:`, error);
                // Add more detailed error information to the alert
                const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
                throw new Error(`Failed to upload ${file.name}: ${errorMessage}`);
            }
        },
		// Prepare image source for display
        prepareImageSource(item) {
            if (item.type.includes('image')) {
                return item.preview;
            }
            else if(item.type.includes('video')) {
                return this.$baseUrl + "/images/svg/file-video.svg";
            }
            else if(item.type.includes('pdf')) {
                return this.$baseUrl + "/images/svg/file-pdf.svg";
            }
            else if(item.type.includes('msword') || item.type.includes('document')) {
                return this.$baseUrl + "/images/svg/file-text.svg";
            }
            else if(item.type.includes('zip')) {
                return this.$baseUrl + "/images/svg/file-archive.svg";
            }
            else {
                return this.$baseUrl + "/images/svg/file-text.svg";
            }
        },
		/**
         * Compress generic files (PDF, DOC, etc.) using browser compression
         * @param {File} file - Original file
         * @returns {Promise<File>} - Compressed file
         */
		 async compressGenericFile(file) {
            try {
                // Read file as array buffer
                const arrayBuffer = await file.arrayBuffer();

                // Use CompressionStream if available (modern browsers)
                if ('CompressionStream' in window) {
                    const compressionStream = new CompressionStream('gzip');
                    const compressedStream = new Response(arrayBuffer).body.pipeThrough(compressionStream);
                    const compressedArrayBuffer = await new Response(compressedStream).arrayBuffer();

                    // Create compressed file
                    const compressedFile = new File(
                        [compressedArrayBuffer],
                        file.name,
                        {
                            type: file.type,
                            lastModified: Date.now()
                        }
                    );

                    return compressedFile;
                } else {
                    // Fallback: Simple byte reduction for older browsers
                    const uint8Array = new Uint8Array(arrayBuffer);

                    // Simple compression: remove redundant bytes (basic approach)
                    const compressed = this.simpleCompress(uint8Array);

                    const compressedFile = new File(
                        [compressed],
                        file.name,
                        {
                            type: file.type,
                            lastModified: Date.now()
                        }
                    );

                    return compressedFile;
                }
            } catch (error) {
                // If compression fails, return original file
                console.error('Compression error:', error);
                return file;
            }
        },

        /**
         * Advanced multi-pass image compression to achieve target file size
         * @param {File} file - Original image file
         * @returns {Promise<File>} - Compressed image file
         */
         async compressImage(file) {
            return new Promise((resolve, reject) => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();
                const objectUrl = URL.createObjectURL(file);

                img.onload = async () => {
                    try {
                        let compressedFile;

                        if (this.compressionSettings.enableMultiPass && this.compressionSettings.aggressiveMode) {
                            // Use advanced multi-pass compression
                            compressedFile = await this.performMultiPassCompression(img, file, canvas, ctx);
                        } else {
                            // Use basic compression
                            compressedFile = await this.performBasicCompression(img, file, canvas, ctx);
                        }

                        // Clean up object URL to prevent memory leaks
                        URL.revokeObjectURL(objectUrl);
                        resolve(compressedFile);
                    } catch (error) {
                        console.error('Compression error:', error);
                        URL.revokeObjectURL(objectUrl);
                        resolve(file); // Return original file if compression fails
                    }
                };

                img.onerror = () => {
                    URL.revokeObjectURL(objectUrl);
                    reject(new Error('Image loading failed'));
                };

                img.src = objectUrl;
            });
        },

        /**
         * Calculate smart initial dimensions based on original file size
         * Always maintain 1920x1080 or closest aspect ratio, don't reduce dimensions
         */
        calculateSmartDimensions(img, originalSize) {
            const maxWidth = this.compressionSettings.imageMaxWidth; // 1920
            const maxHeight = this.compressionSettings.imageMaxHeight; // 1080

            // Calculate aspect ratio
            const aspectRatio = img.width / img.height;

            let targetWidth = maxWidth;
            let targetHeight = maxHeight;

            // Maintain aspect ratio while fitting within 1920x1080
            if (aspectRatio > (maxWidth / maxHeight)) {
                // Image is wider than 16:9 - fit to width
                targetWidth = maxWidth;
                targetHeight = Math.floor(maxWidth / aspectRatio);
            } else {
                // Image is taller than 16:9 - fit to height
                targetHeight = maxHeight;
                targetWidth = Math.floor(maxHeight * aspectRatio);
            }

            // Always return full resolution - don't scale down based on file size
            // Compression will be achieved through quality reduction, not dimension reduction
            return {
                width: targetWidth,
                height: targetHeight
            };
        },

        /**
         * Perform multi-pass compression with iterative optimization
         */
        async performMultiPassCompression(img, originalFile, canvas, ctx) {
            const targetSize = this.compressionSettings.targetFileSize;
            const maxIterations = this.compressionSettings.maxIterations;
            const minQuality = this.compressionSettings.minQuality;

            let bestResult = null;
            let bestSize = Infinity;

            // Calculate smart initial dimensions based on file size
            let dimensions = this.calculateSmartDimensions(img, originalFile.size);
            let currentQuality = this.compressionSettings.imageQuality;

            console.log(`Starting multi-pass compression for ${originalFile.name}:`);
            console.log(`Original: ${(originalFile.size / 1024).toFixed(1)}KB, Target: ${(targetSize / 1024).toFixed(1)}KB`);

            for (let iteration = 0; iteration < maxIterations; iteration++) {
                // Set canvas dimensions
                canvas.width = dimensions.width;
                canvas.height = dimensions.height;

                // Enable high-quality image smoothing
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';

                // Clear and draw image
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, dimensions.width, dimensions.height);

                // Convert to blob
                const blob = await new Promise(resolve => {
                    canvas.toBlob(resolve, this.compressionSettings.imageFormat, currentQuality);
                });

                if (!blob) continue;

                const currentSize = blob.size;
                console.log(`Iteration ${iteration + 1}: ${dimensions.width}x${dimensions.height}, Q:${(currentQuality * 100).toFixed(0)}%, Size: ${(currentSize / 1024).toFixed(1)}KB`);

                // Keep track of best result
                if (currentSize < bestSize) {
                    bestSize = currentSize;
                    bestResult = new File([blob], originalFile.name, {
                        type: this.compressionSettings.imageFormat,
                        lastModified: Date.now()
                    });
                }

                // Check if we've reached our target
                if (currentSize <= targetSize) {
                    console.log(`✓ Target achieved in ${iteration + 1} iterations!`);
                    break;
                }

                // Reduce quality for next iteration
                currentQuality = Math.max(minQuality, currentQuality * 0.9);
            }

            console.log(`Final result: ${(bestSize / 1024).toFixed(1)}KB (${((originalFile.size - bestSize) / originalFile.size * 100).toFixed(1)}% reduction)`);
            return bestResult || originalFile;
        },

        /**
         * Perform basic compression (fallback method)
         */
        async performBasicCompression(img, originalFile, canvas, ctx) {
            // Calculate new dimensions while maintaining aspect ratio
            let { width, height } = this.calculateDimensions(
                img.width,
                img.height,
                this.compressionSettings.imageMaxWidth,
                this.compressionSettings.imageMaxHeight
            );

            // Set canvas dimensions
            canvas.width = width;
            canvas.height = height;

            // Draw and compress image
            ctx.drawImage(img, 0, 0, width, height);

            // Convert to blob with compression
            const blob = await new Promise(resolve => {
                canvas.toBlob(
                    resolve,
                    this.compressionSettings.imageFormat,
                    this.compressionSettings.imageQuality
                );
            });

            if (!blob) {
                throw new Error('Canvas to Blob conversion failed');
            }

            // Create new File object with compressed data
            return new File([blob], originalFile.name, {
                type: this.compressionSettings.imageFormat,
                lastModified: Date.now()
            });
        },

        /**
         * Calculate new dimensions while maintaining aspect ratio
         * @param {number} originalWidth
         * @param {number} originalHeight
         * @param {number} maxWidth
         * @param {number} maxHeight
         * @returns {object} - New width and height
         */
        calculateDimensions(originalWidth, originalHeight, maxWidth, maxHeight) {
            let width = originalWidth;
            let height = originalHeight;

            // Calculate scaling factor
            const widthRatio = maxWidth / originalWidth;
            const heightRatio = maxHeight / originalHeight;
            const ratio = Math.min(widthRatio, heightRatio, 1); // Don't upscale

            width = Math.round(originalWidth * ratio);
            height = Math.round(originalHeight * ratio);

            return { width, height };
        },

        /**
         * Simple compression fallback for older browsers
         * @param {Uint8Array} data - Original data
         * @returns {Uint8Array} - Compressed data
         */
        simpleCompress(data) {
            // Simple run-length encoding for basic compression
            const compressed = [];
            let i = 0;

            while (i < data.length) {
                let count = 1;
                const current = data[i];

                // Count consecutive identical bytes
                while (i + count < data.length && data[i + count] === current && count < 255) {
                    count++;
                }

                if (count > 3) {
                    // Use run-length encoding for sequences > 3
                    compressed.push(255, count, current); // 255 as escape character
                } else {
                    // Keep original bytes for short sequences
                    for (let j = 0; j < count; j++) {
                        compressed.push(current);
                    }
                }

                i += count;
            }

            return new Uint8Array(compressed);
        },
		/**
         * Comprehensive video compression using MediaRecorder API
         * @param {File} file - Original video file
         * @returns {Promise<File>} - Compressed video file
         */
         async compressVideo(file) {
            this.isVideoCompressing = true;
            
            return new Promise((resolve) => {
                const video = document.createElement('video');
                video.src = URL.createObjectURL(file);
                video.muted = true;
                video.playsInline = true;
                
                console.log(`Starting video compression for ${file.name}: ${(file.size / 1024 / 1024).toFixed(1)}MB`);
                
                video.onloadedmetadata = () => {
                    try {
                        const { width, height } = this.calculateVideoDimensions(video.videoWidth, video.videoHeight);
                        console.log(`Original: ${video.videoWidth}x${video.videoHeight}, Compressed: ${width}x${height}`);
                        
                        // Create canvas with optimal settings
                        const canvas = document.createElement('canvas');
                        canvas.width = width;
                        canvas.height = height;
                        
                        const ctx = canvas.getContext('2d', {
                            alpha: false, // Better performance
                            desynchronized: true, // Async rendering
                            powerPreference: 'high-performance' // Use dedicated GPU
                        });
                        
                        // Optimize canvas rendering
                        ctx.imageSmoothingEnabled = true;
                        ctx.imageSmoothingQuality = 'high';
                        
                        // Create stream with target frame rate
                        const stream = canvas.captureStream(this.compressionSettings.videoFrameRate);
                        
                        // Get best codec and bitrate
                        const mimeType = this.getBestVideoMimeType();
                        const bitrate = this.calculateOptimalBitrate(width, height);
                        
                        console.log(`Using codec: ${mimeType}, Bitrate: ${(bitrate / 1000000).toFixed(1)}Mbps`);
                        
                        const options = {
                            mimeType: mimeType,
                            videoBitsPerSecond: bitrate,
                        };

                        const mediaRecorder = new MediaRecorder(stream, options);
                        const chunks = [];
                        
                        mediaRecorder.ondataavailable = (event) => {
                            if (event.data.size > 0) {
                                chunks.push(event.data);
                            }
                        };
                        
                        mediaRecorder.onstop = () => {
                            const compressedBlob = new Blob(chunks, { type: mimeType });
                            const compressedFile = new File([compressedBlob], file.name, {
                                type: compressedBlob.type,
                                lastModified: Date.now()
                            });
                            
                            const compressionRatio = ((file.size - compressedBlob.size) / file.size * 100).toFixed(1);
                            console.log(`✅ Compression completed: ${(file.size / 1024 / 1024).toFixed(1)}MB → ${(compressedBlob.size / 1024 / 1024).toFixed(1)}MB (${compressionRatio}% reduction)`);
                            
                            // Cleanup
                            URL.revokeObjectURL(video.src);
                            this.isVideoCompressing = false;
                            resolve(compressedFile);
                        };
                        
                        mediaRecorder.onerror = (error) => {
                            console.error('MediaRecorder error:', error);
                            URL.revokeObjectURL(video.src);
                            this.isVideoCompressing = false;
                            resolve(file); // Return original on error
                        };
                        
                        // Start recording
                        mediaRecorder.start(1000); // Collect data every second
                        
                        // Fast video rendering - this is the key to speed improvement
                        this.renderVideoFast(video, ctx, width, height, mediaRecorder);
                        
                    } catch (error) {
                        console.error('Video compression setup error:', error);
                        URL.revokeObjectURL(video.src);
                        this.isVideoCompressing = false;
                        resolve(file);
                    }
                };
                
                video.onerror = (error) => {
                    console.error('Video loading error:', error);
                    URL.revokeObjectURL(video.src);
                    this.isVideoCompressing = false;
                    resolve(file);
                };
                
                video.load();
            });
        },

        /**
         * Calculate optimal video dimensions maintaining aspect ratio
         * @param {number} originalWidth - Original video width
         * @param {number} originalHeight - Original video height
         * @returns {object} - Optimized width and height
         */
        calculateVideoDimensions(originalWidth, originalHeight) {
            const maxWidth = this.compressionSettings.videoMaxWidth;
            const maxHeight = this.compressionSettings.videoMaxHeight;

            // Calculate aspect ratio
            const aspectRatio = originalWidth / originalHeight;

            let targetWidth = maxWidth;
            let targetHeight = maxHeight;

            // Maintain aspect ratio while fitting within max dimensions
            if (aspectRatio > (maxWidth / maxHeight)) {
                // Video is wider - fit to width
                targetWidth = maxWidth;
                targetHeight = Math.floor(maxWidth / aspectRatio);
            } else {
                // Video is taller - fit to height
                targetHeight = maxHeight;
                targetWidth = Math.floor(maxHeight * aspectRatio);
            }

            // Ensure dimensions are even numbers (required for some codecs)
            targetWidth = targetWidth % 2 === 0 ? targetWidth : targetWidth - 1;
            targetHeight = targetHeight % 2 === 0 ? targetHeight : targetHeight - 1;

            return {
                width: Math.max(targetWidth, 2),
                height: Math.max(targetHeight, 2)
            };
        },
        
        /**
         * Fast video rendering using optimized playback
         * Key performance improvement: plays video at higher speed while recording at normal speed
         */
        renderVideoFast(video, ctx, width, height, mediaRecorder) {
            video.currentTime = 0;
            
            // Set faster playback rate for quicker processing
            video.playbackRate = 3.0; // Process 3x faster - this is the main speed boost!
            
            const renderFrame = () => {
                if (video.ended) {
                    mediaRecorder.stop();
                    return;
                }
                
                if (video.readyState >= 2) { // Video has enough data
                    ctx.drawImage(video, 0, 0, width, height);
                }
                
                if (!video.ended && !video.paused) {
                    // Use setTimeout for more consistent timing than requestAnimationFrame
                    setTimeout(renderFrame, 1000 / this.compressionSettings.videoFrameRate);
                }
            };
            
            video.oncanplay = () => {
                video.play();
                renderFrame();
            };
        },

        /**
         * Get the most efficient video codec available
         */
        getBestVideoMimeType() {
            const codecs = [
                'video/webm;codecs=vp9,opus',     // Best compression
                'video/mp4;codecs=avc1.42E01E',   // H.264, widely supported
                'video/webm;codecs=vp8,opus',     // Good fallback
                'video/webm',                     // Basic WebM
                'video/mp4'                       // Ultimate fallback
            ];
            
            for (const codec of codecs) {
                if (MediaRecorder.isTypeSupported(codec)) {
                    return codec;
                }
            }
            
            return 'video/webm';
        },

        /**
         * Calculate optimal bitrate based on resolution
         */
        calculateOptimalBitrate(width, height) {
            const pixels = width * height;
            const baseBitrate = this.compressionSettings.videoBitrate;
            
            // Adjust bitrate based on resolution for optimal compression speed
            if (pixels <= 640 * 480) {        // 480p
                return Math.floor(baseBitrate * 0.5);
            } else if (pixels <= 1280 * 720) { // 720p
                return Math.floor(baseBitrate * 0.75);
            } else if (pixels <= 1920 * 1080) { // 1080p
                return baseBitrate;
            } else {                           // Higher than 1080p
                return Math.floor(baseBitrate * 1.25);
            }
        },

        /**
         * Optimized video dimensions calculation
         */
        calculateVideoDimensions(originalWidth, originalHeight) {
            const maxWidth = this.compressionSettings.videoMaxWidth;
            const maxHeight = this.compressionSettings.videoMaxHeight;
            
            // If video is already smaller than target, don't upscale
            if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
                return { width: originalWidth, height: originalHeight };
            }
            
            const aspectRatio = originalWidth / originalHeight;
            
            let targetWidth, targetHeight;
            
            if (aspectRatio > (maxWidth / maxHeight)) {
                // Wider than target aspect ratio - fit to width
                targetWidth = maxWidth;
                targetHeight = Math.floor(maxWidth / aspectRatio);
            } else {
                // Taller than target aspect ratio - fit to height
                targetHeight = maxHeight;
                targetWidth = Math.floor(maxHeight * aspectRatio);
            }
            
            // Ensure dimensions are even (required for most video codecs)
            targetWidth = targetWidth % 2 === 0 ? targetWidth : targetWidth - 1;
            targetHeight = targetHeight % 2 === 0 ? targetHeight : targetHeight - 1;
            
            return {
                width: Math.max(targetWidth, 2),
                height: Math.max(targetHeight, 2)
            };
        }
	}
  };
  </script>

  <style scoped>
  .image-uploader {
	width: 100%;
  }

  .drop-zone {
	min-height: 200px;
	border: 2px dashed #dee2e6;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.3s ease;
  }

  .drag-over {
	border-color: #0d6efd;
	background-color: rgba(13, 110, 253, 0.05);
  }

  .upload-icon-container {
	width: 80px;
	height: 80px;
	margin: 0 auto;
	background-color: rgba(13, 110, 253, 0.1);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
  }

  .upload-icon-container i {
	font-size: 2.5rem;
	color: #0d6efd;
  }

  .image-wrapper {
	position: relative;
	padding-top: 100%; /* 1:1 Aspect Ratio */
	overflow: hidden;
	box-shadow: 0 3px 6px rgba(0,0,0,0.1);
  }

  .preview-image {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
  }

  .preview-item {
	border-radius: 8px;
	overflow: hidden;
	transition: transform 0.2s;
  }

  .preview-item:hover {
	transform: translateY(-3px);
  }

  .preview-info {
	background-color: white;
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
  }

  .add-more {
	border: 2px dashed #dee2e6;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.3s ease;
	min-height: 100px;
  }

  .add-more:hover {
	border-color: #0d6efd;
	background-color: rgba(13, 110, 253, 0.05);
  }

  /* Gallery styling */
  .gallery-item {
	position: relative;
	border-radius: 8px;
	overflow: hidden;
	height: 0;
	padding-top: 100%; /* 1:1 Aspect Ratio */
	box-shadow: 0 4px 8px rgba(0,0,0,0.1);
	transition: transform 0.2s;
  }

  .gallery-item:hover {
	transform: translateY(-3px);
  }

  .gallery-image {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
  }

  .gallery-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 50%);
	opacity: 0;
	transition: opacity 0.3s;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	padding: 10px;
  }

  .show-controls {
	opacity: 1;
  }

  .gallery-info {
	color: white;
	margin-top: auto;
	font-size: 0.8rem;
  }

  .gallery-actions {
	display: flex;
	gap: 8px;
	margin-top: 8px;
	justify-content: flex-end;
  }

  .quick-action-btn {
	opacity: 0.5;
	transition: opacity 0.2s;
  }

  .gallery-item:hover .quick-action-btn {
	opacity: 1;
  }

  /* Toast notification */
  .toast.show {
	display: block;
	opacity: 1;
  }

  .toast {
	transition: opacity 0.3s ease;
	display: none;
	opacity: 0;
  }

  /* Add fade-in animation for new files */
  .preview-item {
	animation: fadeIn 0.3s;
  }

  @keyframes fadeIn {
	from { opacity: 0; transform: translateY(10px); }
	to { opacity: 1; transform: translateY(0); }
  }

  .file-progress-card {
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
	transition: transform 0.2s;
  }

  .file-progress-card:hover {
	transform: translateY(-3px);
  }

  .image-preview {
	position: relative;
	height: 120px;
	overflow: hidden;
	border-radius: 4px;
  }

  .image-preview img {
	width: 100%;
	height: 100%;
	object-fit: cover;
  }

  .progress-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(0,0,0,0.3);
	color: white;
	font-weight: bold;
	font-size: 1.2rem;
  }

  .progress-percentage {
	background-color: rgba(0,0,0,0.5);
	padding: 4px 10px;
	border-radius: 20px;
  }
  </style>
